import { useEffect, useState } from 'react';
import type { Project } from '@forecastify/shared';
import { useWorkspaceNavigate } from '../../workspace-path';
import { managesProject, useMyMemberId, usePermissionSet } from '../permissions-hook';
import { InvoiceList } from './invoice-list';
import { baseCurrencyOf } from '../base-currency';
import { InvoiceDialog, draftOf, emptyDraft, type InvoiceDraft } from './invoice-dialog';
import { useInvoices } from './use-invoices';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A projekt Számlák fülje — C57.                                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The workspace list without the *Projekt* column and its filter**, which is exactly the
 * difference the mockup draws between `invColumnsAll` and `invColumnsProj`. `InvoiceList` takes the
 * projects or does not, and that one prop is the whole distinction.
 *
 * ⚠️ **`Új számla` EXISTS here and not on the workspace list.** A project narrows the line-item
 * picker to a handful of deliverables somebody recognises; the workspace-wide form would ask them
 * to find one by name among every contract in the business, which is why the mockup's own empty
 * state there says to go to the deliverable instead.
 */
export function ProjectInvoicesTab({ project }: { readonly project: Project }) {
  const go = useWorkspaceNavigate();
  const permissions = usePermissionSet();
  const data = useInvoices({ projectId: project.id });

  const [editing, setEditing] = useState<InvoiceDraft | null>(null);
  const [periods, setPeriods] = useState<readonly { startsOn: string; label: string }[]>([]);

  const pickedContract = editing?.contractId ?? '';
  useEffect(() => {
    void (async () => {
      setPeriods(await data.periodsOf(pickedContract));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `data` is rebuilt every render. */
  }, [pickedContract]);

  /* 22 Oct 2026 · at `own`, only on a project the reader is PM of (`project-manage.ts`). */
  const myMemberId = useMyMemberId();
  const canManage = managesProject(permissions, 'contract.manage', project.pmMemberId, myMemberId);
  /* ⚠️ The workspace's base currency where there is one, so a new invoice starts in the unit every
     other figure on this project is already shown in. The server overrules it from the source. */
  const baseCode = baseCurrencyOf(data.currencies)?.code ?? 'HUF';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {data.serverError === null ? null : (
        <p className="text-destructive px-4 pt-2 text-[12px]">{data.serverError}</p>
      )}

      <InvoiceList
        projectId={project.id}
        nonce={data.revision}
        currencies={data.currencies}
        handlers={{
          canManage,
          onOpen: (invoice) => {
            go(`/invoices/${invoice.identifier}`);
          },
          onEdit: (invoice) => {
            setEditing(draftOf(invoice));
          },
          onStatus: (invoice, status) => {
            void data.setStatus(invoice, status);
          },
          onDelete: (invoice) => {
            void data.remove(invoice);
          },
          onNew: () => {
            setEditing(emptyDraft(baseCode, data.members[0]?.id ?? ''));
          },
        }}
      />

      {editing === null ? null : (
        <InvoiceDialog
          draft={editing}
          lineItems={data.lineItems}
          contracts={data.contracts}
          periods={periods}
          members={data.members}
          remainingLabel=""
          serverError={data.serverError}
          onCancel={() => {
            setEditing(null);
            data.clearError();
          }}
          onSave={(body, closeSource) => {
            void (async () => {
              const saved = await data.save(editing.id, body, closeSource);
              if (saved !== null) setEditing(null);
            })();
          }}
        />
      )}
    </div>
  );
}
